import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Configuration › Limit policies (`cf-limits`) and Money › VAT rates, read from
 * `GET /v1/admin/limit-policies` and `GET /v1/admin/vat/rates`
 * (handlers `internal/server/admin_limitpolicy.go`, `admin_vat.go`).
 *
 * # `live` and `governs_now` differ for the length of every notice period
 *
 * Both catalogues carry the pair, and the VAT endpoint states the failure:
 *
 *   > a rate published to take effect next quarter is OPEN while the incumbent
 *   > is closed at a future instant, so a console rendering "in force" from
 *   > `live` alone shows the wrong rate exactly when somebody is looking.
 *
 * "Exactly when somebody is looking" is the part that matters: the two agree
 * except during the window where an operator is most likely to be checking. So
 * "in force" is read from `governs_now` everywhere here, and `live` is rendered
 * as what it is — open-ended.
 *
 * # Closed intervals are the point of the VAT screen, not clutter
 *
 * "Invariant 9's acceptance test is that an operator holding only a citation
 * reconstructs the outcome six months later; a list showing only what governs
 * today would hide every rate any past receipt cited." So a superseded interval
 * renders in full, labelled superseded rather than dimmed away.
 *
 * # Rates are basis points, and stay basis points
 *
 * `2100` is 21%. It is an integer "because the arithmetic is int64 minor units
 * throughout and money is never a float" (law 9). Converting to a percentage
 * for display invites a decimal, and the decimal is how a float creeps back in.
 * The percentage is offered alongside as a reading aid, derived exactly from the
 * integer and never replacing it.
 *
 * # An empty window list means 24/7 and is never null
 *
 * "Null would read as 'unknown', and a console telling an operator the permitted
 * hours were unknown would send them to look for a screen that does not exist."
 * So an empty array renders as `always`, and a genuinely absent array renders as
 * not stated.
 *
 * # Tier 0 is a real tier
 *
 * `tier` is nullable precisely because 0 is the floor every customer starts at —
 * "so a plain integer would make 'tier 0 gets EUR 500 a day' and 'I forgot the
 * field' the same request". A policy scoped to tier 0 renders as tier 0.
 *
 * # The timezone is never defaulted to UTC, and the screen shows it
 *
 * It anchors the period boundaries, and "a UTC anchor rolls a Sofia customer's
 * day at 02:00 local in summer — a daily limit resetting at an hour nobody
 * agreed to". It is a column rather than a footnote.
 *
 * # Read-only
 *
 * There is one write on each catalogue and it is a publish, never an edit: a
 * published version is immutable by trigger and a change is a new version that
 * closes the incumbent (law 6, invariant 9). Neither is called here.
 */

export const LIMIT_POLICIES_PATH = '/limit-policies'
export const VAT_RATES_PATH = '/vat/rates'

const LIMITS_SOURCE = 'postgres · limit_policy_versions (GET /v1/admin/limit-policies)'
const VAT_SOURCE = 'postgres · vat_rates (GET /v1/admin/vat/rates)'
const AUTHORITY = 'operational · immutable versions, closed by their successor'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function day(at: string): string {
  return at === '' ? '' : at.slice(0, 10)
}

/**
 * Basis points, with the percentage beside them.
 *
 * The integer is the value; the percentage is a reading aid derived exactly
 * from it. Rendering only the percentage would put a decimal where the system
 * keeps an integer, and the decimal is where a float gets back in.
 */
function bpsText(bps: number | null): string {
  if (bps === null) return '— not stated'
  const whole_ = Math.trunc(bps / 100)
  const frac = Math.abs(bps % 100)
  const percent = frac === 0 ? `${String(whole_)}%` : `${String(whole_)}.${String(frac).padStart(2, '0')}%`
  return `${String(bps)} bps (${percent})`
}

function amountCell(minor: unknown, currency: string): Cell {
  if (!isCurrencyCode(currency)) return '— no currency on this policy'
  const value = exactMinorBig(minor)
  if (value === null) return '— not an exact integer'
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return '— beyond the exact integer range of an amount cell'
  }
  return [Number(value), currency]
}

/**
 * The in-force chip.
 *
 * Read from `governs_now`. `live` is reported separately as "open-ended",
 * because the two differ for the length of every notice period and that is
 * exactly when somebody is looking.
 */
function forceChip(governsNow: boolean | null, live: boolean | null): ChipCell {
  if (governsNow === true) return ['in force now', 'g']
  if (governsNow === false) {
    return live === true ? ['published, not yet in force', 'a'] : ['superseded', 'n']
  }
  return ['— not stated', 'r']
}

// ---------------------------------------------------------------------------
// cf-limits
// ---------------------------------------------------------------------------

interface Policy {
  readonly id: number | null
  readonly code: string
  readonly version: number | null
  readonly scope: string
  readonly customerId: number | null
  /** NULLABLE because 0 is a REAL tier — the floor every customer starts at. */
  readonly tier: number | null
  readonly direction: string
  readonly currency: string
  readonly timezone: string
  readonly minPerTxn: unknown
  readonly maxPerTxn: unknown
  readonly caps: readonly string[]
  /** `null` when the array was absent; `[]` means 24/7 and is never null. */
  readonly windows: readonly string[] | null
  readonly live: boolean | null
  readonly governsNow: boolean | null
  readonly effectiveFrom: string
  readonly publishedBy: string
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function minuteOfDay(m: number): string {
  const h = Math.floor(m / 60)
  return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

function readPolicy(raw: unknown): Policy | null {
  if (!isObject(raw)) return null
  const code = text(raw['code'])
  if (code === '') return null

  const caps: string[] = []
  if (Array.isArray(raw['caps'])) {
    for (const c of raw['caps'] as readonly unknown[]) {
      if (!isObject(c)) continue
      const period = text(c['period'])
      const amount = exactMinorBig(c['max_amount_minor'])
      const count = whole(c['max_count'])
      caps.push(
        `${period === '' ? 'unnamed period' : period}: ${amount === null ? '— no amount' : String(amount)} minor, ${count === null ? '— no count' : String(count)} txn`,
      )
    }
  }

  let windows: string[] | null = null
  if (Array.isArray(raw['windows'])) {
    windows = []
    for (const w of raw['windows'] as readonly unknown[]) {
      if (!isObject(w)) continue
      const days = Array.isArray(w['days'])
        ? (w['days'] as readonly unknown[])
            .map((d) => whole(d))
            // SUNDAY = 0, time.Weekday's encoding rather than ISO-8601's. The
            // two differ by exactly one, and a window opening on six days
            // nobody chose is invisible in any test that lands on a Wednesday.
            .map((d) => (d === null ? '?' : (DAY_NAMES[d] ?? `day ${String(d)}`)))
            .join(' ')
        : '—'
      const open = whole(w['open_minute'])
      const close = whole(w['close_minute'])
      windows.push(
        `${days} ${open === null ? '—' : minuteOfDay(open)}–${close === null ? '—' : minuteOfDay(close)}`,
      )
    }
  }

  return {
    id: whole(raw['id']),
    code,
    version: whole(raw['version']),
    scope: text(raw['scope']),
    customerId: whole(raw['customer_id']),
    tier: whole(raw['tier']),
    direction: text(raw['direction']),
    currency: text(raw['currency']),
    timezone: text(raw['timezone']),
    minPerTxn: raw['min_per_txn_minor'],
    maxPerTxn: raw['max_per_txn_minor'],
    caps,
    windows,
    live: bool(raw['live']),
    governsNow: bool(raw['governs_now']),
    effectiveFrom: text(raw['effective_from']),
    publishedBy: text(raw['published_by']),
  }
}

/** Who a version governs. Tier 0 is a tier and renders as one. */
function subjectCell(p: Policy): string {
  if (p.scope === 'default') return 'every customer (default)';
  if (p.scope === 'tier') return p.tier === null ? 'tier — NOT STATED' : `tier ${String(p.tier)}`
  if (p.scope === 'customer') {
    return p.customerId === null ? 'one customer — id not stated' : `customer ${String(p.customerId)}`
  }
  return p.scope === '' ? '— scope not stated' : `unknown scope: ${p.scope}`
}

const POLICY_COLS: readonly Column[] = [
  ['Code', 'm', 'minmax(160px,1.1fr)'],
  ['Version', 'm', '90px'],
  ['Governs', 'x', 'minmax(150px,1.1fr)'],
  ['Direction', 'm', '96px'],
  ['Per transaction', 'a', '160px'],
  ['Timezone', 'm', '150px'],
  ['Permitted hours', 'x', 'minmax(170px,1.2fr)'],
  ['In force', 'c', '180px'],
]

function policyRow(p: Policy): readonly Cell[] {
  return [
    p.code,
    p.version === null ? '—' : `v${String(p.version)}`,
    subjectCell(p),
    // Inbound and outbound ceilings are unrelated numbers and are never one
    // policy, so the direction is never implied.
    p.direction === '' ? '— not stated' : p.direction,
    amountCell(p.maxPerTxn, p.currency),
    // Never defaulted to UTC by this console either: it anchors the period
    // boundaries, and a wrong anchor resets a daily limit at an hour nobody
    // agreed to.
    p.timezone === '' ? '— NOT STATED' : p.timezone,
    p.windows === null
      ? '— not stated'
      : p.windows.length === 0
        ? 'always — 24/7'
        : p.windows.join(' · '),
    forceChip(p.governsNow, p.live),
  ]
}

export function composeLimitPolicies(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['policies']) ? (payload['policies'] as readonly unknown[]) : []
  const rows: Policy[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readPolicy(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const inForce = rows.filter((p) => p.governsNow === true).length
  const pending = rows.filter((p) => p.governsNow === false && p.live === true).length
  const noZone = rows.filter((p) => p.timezone === '')

  const table: TableBlock = {
    t: 'table',
    title: 'limit_policy_versions',
    src: 'ledger',
    facets: false,
    cols: POLICY_COLS,
    rows: rows.map(policyRow),
    footer: `${String(rows.length)} version(s) · ${String(inForce)} in force${pending === 0 ? '' : ` · ${String(pending)} published and not yet in force`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      '"In force" is read from `governs_now`, never from `live`: a version published to take effect next quarter is open while the incumbent is closed at a future instant, and the two differ for the length of every notice period — which is exactly when somebody is looking. An empty permitted-hours list means 24/7 and is never null.',
  }

  const blocks: Block[] = [table]

  if (noZone.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'No timezone',
      title: `${String(noZone.length)} version(s) state no timezone`,
      body: 'The timezone anchors the period boundaries and is never defaulted to UTC — a UTC anchor rolls a Sofia customer’s day at 02:00 local in summer, resetting a daily limit at an hour nobody agreed to. A version without one has period boundaries this console cannot state.',
      ref: noZone.map((p) => `${p.code} v${String(p.version ?? 0)}`).join(' · '),
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Limit policies',
      title: 'No limit policy version came back',
      body: 'Unlimited is a decision rather than a default. If nothing is published, authorisation evaluates entitlements and typologies alone — which is a policy position somebody should have taken deliberately.',
      ref: 'limit_policy_versions · the read returned no rows',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} version(s) could not be read`,
      body: 'Counted rather than dropped. A missing version is a ceiling nobody can see being enforced.',
      ref: 'a version with no code governs nothing nameable and is refused',
    })
  }

  blocks.push({
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Law 6',
    title: 'A published version is immutable, and publishing is not wired here',
    body: 'There is one write on this catalogue and it is a publish, never an edit: a version is immutable by trigger and a change is a NEW version that closes the incumbent at the new boundary. The scope is explicit rather than inferred from which subject field is populated — a console that forgot the customer id would otherwise publish a product-wide default while believing it wrote one person’s override.',
    ref: 'POST /v1/admin/limit-policies is untouched',
  })

  return {
    blocks,
    provenance: { kind: 'live', source: LIMITS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: rows.map((p) => (p.id === null ? null : String(p.id))),
  }
}

// ---------------------------------------------------------------------------
// VAT rates
// ---------------------------------------------------------------------------

interface Rate {
  readonly id: number | null
  readonly country: string
  readonly supplyKind: string
  readonly version: number | null
  readonly treatment: string
  readonly rateBps: number | null
  readonly live: boolean | null
  readonly governsNow: boolean | null
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly publishedBy: string
  readonly reasonCode: string
}

function readRate(raw: unknown): Rate | null {
  if (!isObject(raw)) return null
  const id = whole(raw['id'])
  if (id === null) return null
  return {
    id,
    country: text(raw['country']),
    supplyKind: text(raw['supply_kind']),
    version: whole(raw['version']),
    treatment: text(raw['treatment']),
    rateBps: whole(raw['rate_bps']),
    live: bool(raw['live']),
    governsNow: bool(raw['governs_now']),
    effectiveFrom: text(raw['effective_from']),
    effectiveTo: text(raw['effective_to']),
    publishedBy: text(raw['published_by']),
    reasonCode: text(raw['reason_code']),
  }
}

const RATE_COLS: readonly Column[] = [
  ['Rate', 'm', '80px'],
  ['Country', 'm', '90px'],
  ['Supply', 'x', 'minmax(150px,1.1fr)'],
  ['Version', 'm', '90px'],
  ['Treatment', 'x', '150px'],
  ['Rate', 'x', '170px'],
  ['Effective', 'm', '190px'],
  ['In force', 'c', '180px'],
]

function rateRow(r: Rate): readonly Cell[] {
  return [
    r.id === null ? '— no id' : String(r.id),
    r.country === '' ? '—' : r.country,
    r.supplyKind === '' ? '— not stated' : r.supplyKind,
    r.version === null ? '—' : `v${String(r.version)}`,
    r.treatment === '' ? '— not stated' : r.treatment,
    // Basis points, with the percentage as a reading aid derived exactly.
    bpsText(r.rateBps),
    `${day(r.effectiveFrom) === '' ? '—' : day(r.effectiveFrom)} → ${day(r.effectiveTo) === '' ? 'open' : day(r.effectiveTo)}`,
    forceChip(r.governsNow, r.live),
  ]
}

export function composeVATRates(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['rates']) ? (payload['rates'] as readonly unknown[]) : []
  const rows: Rate[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readRate(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const superseded = rows.filter((r) => r.governsNow === false && r.live !== true).length
  const pending = rows.filter((r) => r.governsNow === false && r.live === true).length

  const table: TableBlock = {
    t: 'table',
    title: 'vat_rates · every interval, newest version first',
    src: 'ledger',
    facets: false,
    cols: RATE_COLS,
    rows: rows.map(rateRow),
    footer: `${String(rows.length)} interval(s)${superseded === 0 ? '' : ` · ${String(superseded)} superseded and kept`}${pending === 0 ? '' : ` · ${String(pending)} published and not yet in force`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'Closed intervals are the point of this screen rather than clutter: an operator holding only a citation must be able to reconstruct a receipt six months later, and a list showing only what governs today would hide every rate any past receipt cited. Rates are basis points — 2100 is 21% — because the arithmetic is integer minor units throughout.',
  }

  const blocks: Block[] = [table]

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'VAT rates',
      title: 'No published interval came back',
      body: 'Every row here is a public fact about a member state’s law rather than anything about a person, and a receipt cites a rate id and a version. With nothing published, no receipt can cite anything — which is a state worth resolving before a fee is charged anywhere.',
      ref: 'vat_rates · the read returned no rows',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} interval(s) could not be read`,
      body: 'Counted rather than dropped. A missing interval is a receipt somebody cannot reconstruct.',
      ref: 'an interval with no id cannot be cited and is refused',
    })
  }

  blocks.push({
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Law 6 · invariant 9',
    title: 'An interval is immutable, and publishing is not wired here',
    body: 'A change is a NEW interval that closes the incumbent at the new boundary, which is why there is no PUT and no DELETE anywhere on this surface — and why a receipt issued last quarter still reproduces at the rate that applied then.',
    ref: 'POST /v1/admin/vat/rates is untouched',
  })

  return {
    blocks,
    provenance: { kind: 'live', source: VAT_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: rows.map((r) => (r.id === null ? null : String(r.id))),
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(what: string, source: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be read`,
      body: `${reason} Nothing below is known — and an unread catalogue is not an empty one. A ceiling nobody can see is still being enforced, and a rate nobody can see is still on somebody’s receipt.`,
      ref: decisionRef === null ? 'Nothing was read.' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

function catalogueLoader(
  what: string,
  path: string,
  source: string,
  compose: (payload: Record<string, unknown>, readAt: Date) => ScreenData,
): ScreenLoader {
  return async (api) => {
    let payload: unknown
    try {
      payload = await api.get<unknown>(path)
    } catch (error) {
      if (error instanceof ApiError) {
        return unreadableScreen(
          what,
          source,
          `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
          error.decisionRef,
        )
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return unreadableScreen(what, source, 'The backend could not be reached.', null)
    }
    if (!isObject(payload)) {
      return unreadableScreen(what, source, 'The endpoint returned a shape this console does not know.', null)
    }
    return compose(payload, new Date())
  }
}

export const loadLimitPolicies = catalogueLoader(
  'Limit policies', LIMIT_POLICIES_PATH, LIMITS_SOURCE, composeLimitPolicies,
)
export const loadVATRates = catalogueLoader('VAT rates', VAT_RATES_PATH, VAT_SOURCE, composeVATRates)
